import Vue from "vue";
import VueI18n from "vue-i18n";
import VueRouter, { NavigationGuard, Route } from 'vue-router'
import { Store } from 'vuex'

import { BaseTemplateConfig } from "../../NewConfig";
import { CompanyProduct, ProductNames } from '../../components/OurProducts/types'
import TemplateContext from '../TemplateContext'
import { ConfigMenuItem } from './MenuItem'
import { InvoiceInfoWithTargets } from "./Shared/InvoiceInfo.types";

export type PaymentTargetConfig = {
  target_id: Record<string, Array<number | string> | string>
  target_type?: string
}

export type Meta = {
  projectName: string
  apiUrl: string
  projectDomain: string
  sidebarLogo?: string
  loginPageLogo?: string
  languageCodes: Array<string>
  isSaveButtonVisible?: boolean
  tgResolvers?: {
    supportBotDomain?: string
    promotionBotDomain?: string
    mainBotDomain?: string
  }
  login: {
    botAuthLink: string
    loginWidget: {
      tgWidgetAuthLink: string
      botLogin: string
    }
  }
  paymentEntity?: {
    target_type: (ctx: TemplateContext) => PaymentTargetConfig
  }
}

export type Tariff = {
  type: string
  img: string
  color: string
  id?: number
}

type ServiceData = {
  type: 'ServiceData'
  tariffs: Record<string, Tariff>
}

type GlobalGuards = {
  afterEach?: (to: Route) => void,
  beforeEach: NavigationGuard
}

type LoyaltySystem = {
  partnerBlock: { partnerLink: (localeCode: string) => string }
  companiesBlock: { termsOwnerLink: (localeCode: string) => string }
}

type Profile = {
  advertiseButton?: (localeCode: string) => string
  titleHelpMessage?: () => any
  recurringInvoices?: {
    getRecurringInvoicesReq: () => Promise<Array<InvoiceInfoWithTargets>>
    deleteRecurringInvoiceReq: (invoice: InvoiceInfoWithTargets, vm: Vue) => Promise<number | void>
  }
  extraAuth?: boolean
}

type CompanyRegistration = {
  powerOfAttorney?: (localeCode: string) => string
  howToRegisterUr?: (localeCode: string) => string
}

export type Footer = {
  version?: number
  ourProducts?: {
    visible?: boolean,
    exclude?: Array<ProductNames>
    settings?: Partial<Record<ProductNames, Partial<CompanyProduct>>>
  }
}

export type SearchBarItem = {
  avatar?: string
  title: string
  id: number | string
}

export type TemplateConfig = {
  meta: Meta
  footer: Footer
  sidebarMenu?: Array<Record<'menuItem', ConfigMenuItem> | (() => Array<Record<'menuItem', ConfigMenuItem>>)>
  loyaltySystem?: LoyaltySystem
  profile?: Profile
  companyRegistration?: CompanyRegistration
  rightScreenButton?: { link: (localeCode: string) => string }
  serviceData?: ServiceData
  topMenu?: Array<Record<'menuItem', ConfigMenuItem>>
  feedbackItems?: Array<Record<'menuItem', ConfigMenuItem>>
  globalGuards?: GlobalGuards
  onSaveButtonClick?: (ctx: TemplateContext, wasClicked: boolean) => void
  onLogin?: (isLogin: boolean) => void
  onLogout?: (isLogin: boolean) => void
  onEntityPathLeave?: (isLeave: boolean) => void
  search?: {
    entities: (ctx: TemplateContext) => Array<SearchBarItem> | Promise<Array<SearchBarItem>>
    path: (ctx: TemplateContext, router: VueRouter, selectedId: SearchBarItem['id'] | null) => void
  }
} & BaseTemplateConfig

export type AdminTemplateOptions = {
  config: TemplateConfig
  router: VueRouter
  store: Store<any>
  i18n?: VueI18n
}

export enum Layout {
  Fullscreen = 'Fullscreen',
  Panel = 'Panel',
  SidebarPanel = 'SidebarPanel'
}

export const layoutViews = {
  [Layout.Fullscreen]: (): any => import('../../layouts/Fullscreen.vue'),
  [Layout.Panel]: (): any => import('../../layouts/Panel.vue'),
  [Layout.SidebarPanel]: (): any => import('../../layouts/SidebarPanel.vue'),
}
